import React from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Headset } from "lucide-react";
import { useAuth } from "../AuthContext";
import {
  API_UNREACHABLE_MESSAGE,
  apiHost,
  isApiConnectionFailure,
} from "../../../shared/api/helpers";
import { requestDesktopForgetAccount, requestDesktopLogin, requestDesktopSavedAccounts, requestDesktopSavedLogin, supportsDesktopNativeAuth } from "../desktopNativeAuth";
import { openExternalUrl, resolveDesktopDeviceId } from "../desktopProxy";
import { ThemeToggle } from "../../../shared/ui/ThemeToggle";
import { toast } from "../../../shared/ui/toast";

interface LoginFormInputs {
    username: string;
    password: string;
    remember: boolean;
}

type LoginErrorData = {
    message?: unknown;
    detail?: unknown;
};

const LOGIN_ERROR_FALLBACK = "시스템 오류입니다. 잠시 후 다시 시도하세요";

function getStringMessage(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value : null;
}

export function getLoginFailureMessage(error: unknown): string {
    const ax = error as {
        response?: { status?: number; data?: LoginErrorData };
    };
    const response = ax.response;
    if (
        isApiConnectionFailure(error)
        && (!response || [502, 503, 504].includes(response.status ?? 0))
    ) {
        return API_UNREACHABLE_MESSAGE;
    }
    return (
        getStringMessage(ax.response?.data?.message)
        || getStringMessage(ax.response?.data?.detail)
        || LOGIN_ERROR_FALLBACK
    );
}

const LoginPage: React.FC = () => {

    const auth = useAuth();
    const navigate = useNavigate();

    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormInputs>();
    const [formErrorMessage, setFormErrorMessage] = React.useState<string | null>(null);
    const [formProcessingMessage, setFormProcessingMessage] = React.useState<string | null>(null);
    const isDesktopShell = typeof window !== "undefined" && window.parent !== window;
    const [savedAccounts, setSavedAccounts] = React.useState<string[]>([]);
    const [selectedAccount, setSelectedAccount] = React.useState("");
    const [nativeAuthAvailable, setNativeAuthAvailable] = React.useState(false);

    React.useEffect(() => {
        if (!isDesktopShell) return;
        let active = true;
        void supportsDesktopNativeAuth()
            .then(async (supported) => {
                if (!active) return;
                setNativeAuthAvailable(supported);
                if (!supported) return;
                const accounts = await requestDesktopSavedAccounts();
                if (!active) return;
                setSavedAccounts(accounts);
                setSelectedAccount(accounts[0] || "");
            })
            .catch((error) => console.error("Saved desktop accounts unavailable:", error));
        return () => { active = false; };
    }, [isDesktopShell]);

    const loginSaved = () => {
        if (!selectedAccount) return;
        setFormErrorMessage(null);
        setFormProcessingMessage("로그인 중입니다…");
        void requestDesktopSavedLogin(selectedAccount)
            .then((access) => auth.loginDesktop(access))
            .catch((error: unknown) => setFormErrorMessage(error instanceof Error ? error.message : LOGIN_ERROR_FALLBACK))
            .finally(() => setFormProcessingMessage(null));
    };

    const forgetSaved = () => {
        if (!selectedAccount || !window.confirm(`${selectedAccount} 계정의 저장된 비밀번호를 삭제하시겠습니까?`)) return;
        setFormErrorMessage(null);
        void requestDesktopForgetAccount(selectedAccount)
            .then(() => {
                const remaining = savedAccounts.filter((account) => account !== selectedAccount);
                setSavedAccounts(remaining);
                setSelectedAccount(remaining[0] || "");
            })
            .catch((error: unknown) => setFormErrorMessage(error instanceof Error ? error.message : LOGIN_ERROR_FALLBACK));
    };


    React.useEffect(() => {
        if (auth.isAuthenticated) {
            navigate("/"); // React Router의 navigate 사용
        }
    }, [auth.isAuthenticated, navigate]);


    const onSubmit = (data: LoginFormInputs) => {
        setFormErrorMessage(null); // Reset error state
        setFormProcessingMessage("로그인 중입니다…"); // Set loading state
        void (async () => {
          let nativeAuth = false;
          try {
            nativeAuth = isDesktopShell && (nativeAuthAvailable || await supportsDesktopNativeAuth());
            if (nativeAuth) {
              const result = await requestDesktopLogin(data.username, data.password, data.remember);
              auth.loginDesktop(result.access);
              if (result.warning) toast.error(result.warning);
              return;
            }
            const deviceId = isDesktopShell ? await resolveDesktopDeviceId() : null;
            const response = await axios.post(`${apiHost}/api/token/`, {
              username: data.username,
              password: data.password,
              client: isDesktopShell ? "desktop" : "web",
              ...(deviceId ? { device_id: deviceId } : {}),
            });
            const body = response.data;
            if (body.success === false) {
              await auth.logout();
              setFormErrorMessage(getLoginFailureMessage({ response: { data: body } }));
              return;
            }
            if (body.access && body.refresh) {
              auth.login(body);
              return;
            }
            await auth.logout();
            setFormErrorMessage(LOGIN_ERROR_FALLBACK);
          } catch (error: unknown) {
            if (!nativeAuth) await auth.logout();
            setFormErrorMessage(nativeAuth && error instanceof Error ? error.message : getLoginFailureMessage(error));
            console.error(error);
          } finally {
            setFormProcessingMessage("");
          }
        })();
    };

    return (
        <div className="auth-shell">
            <div className="auth-theme"><ThemeToggle /></div>
            <aside className="auth-aside">
                <div className="auth-brand"><span className="brand-dot" aria-hidden="true" />nprice</div>
                <p className="auth-lede">가격 비교 · 조정 · 작업이력</p>
            </aside>
            <div className="auth-panel">
                <h1 className="auth-title">로그인</h1>
                {formErrorMessage && (
                    <div className="alert alert-danger" role="alert">
                        {formErrorMessage}
                    </div>
                )}
                {formProcessingMessage && (
                    <div className="alert alert-info" role="alert">
                        로그인 중입니다…
                    </div>
                )}
                {isDesktopShell && nativeAuthAvailable && savedAccounts.length > 0 && (
                    <div className="mb-4">
                        <label htmlFor="saved-desktop-account" className="form-label">저장된 계정</label>
                        <select id="saved-desktop-account" className="form-select mb-2" value={selectedAccount} onChange={(event) => setSelectedAccount(event.target.value)}>
                            {savedAccounts.map((account) => <option key={account} value={account}>{account}</option>)}
                        </select>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-outline-primary flex-grow-1" onClick={loginSaved} disabled={Boolean(formProcessingMessage)}>저장된 계정으로 로그인</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={forgetSaved} disabled={Boolean(formProcessingMessage)}>삭제</button>
                        </div>
                    </div>
                )}
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <ul className="list-unstyled">
                        <li className="mb-3">
                            <label htmlFor="id_username" className="form-label">사용자명</label>
                            <input
                                type="text"
                                autoComplete="username"
                                id="id_username"
                                className={`form-control ${errors.username ? "is-invalid" : ""}`}
                                {...register("username", { required: "사용자명을 입력하세요.", minLength: 4, maxLength: 16 })}
                            />
                            {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
                        </li>
                        <li className="mb-3">
                            <label htmlFor="id_password" className="form-label">비밀번호</label>
                            <input
                                type="password"
                                autoComplete="current-password"
                                id="id_password"
                                className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                {...register("password", { required: "비밀번호를 입력하세요." })}
                            />
                            {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                        </li>
                        {isDesktopShell && nativeAuthAvailable && <li className="mb-3"><label><input type="checkbox" {...register("remember")} /> 이 계정의 비밀번호 저장</label></li>}
                    </ul>
                    <button type="submit" className="btn btn-primary w-100" disabled={Boolean(formProcessingMessage)}>로그인</button>
                </form>
                <button
                    type="button"
                    className="btn btn-outline-secondary w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => openExternalUrl("http://helppc.net/nprice")}
                >
                    <Headset size={18} aria-hidden="true" />
                    원격지원
                </button>
            </div>
        </div>
    );

};

export default LoginPage;
